import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/standard-button';
import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { SnippetSortOrder } from '../constants/snippetSortOrder.ts';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		alignItems: 'baseline',
	},
	dropdownContainer: {
		width: '200px',
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
	},
});

const messages = defineMessages({
	sortByLabel: {
		id: 'snippets.panel.sort-by.label',
		defaultMessage: 'Sort by',
		description: 'Label shown next to the sort order dropdown for block templates',
	},
	createdAtLabel: {
		id: 'snippets.panel.sort-by.option.created-at.label',
		defaultMessage: 'Created at',
		description: 'Sort order option to sort block templates by creation date (newest first)',
	},
	lastUpdatedLabel: {
		id: 'snippets.panel.sort-by.option.last-updated.label',
		defaultMessage: 'Last updated',
		description: 'Sort order option to sort block templates by last updated date (newest first)',
	},
	popularityLabel: {
		id: 'snippets.panel.sort-by.option.popularity.label',
		defaultMessage: 'Popularity',
		description: 'Sort order option to sort block templates by popularity (most to least)',
	},
	alphabeticalLabel: {
		id: 'snippets.panel.sort-by.option.alphabetical.label',
		defaultMessage: 'Alphabetical',
		description: 'Sort order option to sort block templates alphabetically (A-Z)',
	},
});

const optionLabels: Record<SnippetSortOrder, React.ReactNode> = {
	[SnippetSortOrder.CREATED_AT]: (
		<FormattedMessage {...messages.createdAtLabel} key={messages.createdAtLabel.id} />
	),
	[SnippetSortOrder.LAST_UPDATED]: (
		<FormattedMessage {...messages.lastUpdatedLabel} key={messages.lastUpdatedLabel.id} />
	),
	[SnippetSortOrder.POPULARITY]: (
		<FormattedMessage {...messages.popularityLabel} key={messages.popularityLabel.id} />
	),
	[SnippetSortOrder.ALPHABETICAL]: (
		<FormattedMessage {...messages.alphabeticalLabel} key={messages.alphabeticalLabel.id} />
	),
};

type SnippetSortDropdownProps = {
	onChange: (sortOrder: SnippetSortOrder) => void;
	value: SnippetSortOrder;
	isDisabled?: boolean;
};

export const SnippetSortDropdown: FC<SnippetSortDropdownProps> = ({
	onChange,
	value,
	isDisabled = false,
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOpenChange = ({ isOpen }: { isOpen: boolean }) => {
		setIsMenuOpen(isOpen);
	};

	const handleSortOrderChange = (selectedSortOption: SnippetSortOrder) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.FILTER,
				action: ANALYTICS_ACTION.CLICKED,
				actionSubjectId: ANALYTICS_SUBJECT_ID.SORT_ORDER,
				attributes: {
					previousSortingScheme: value,
					newSortingScheme: selectedSortOption,
				},
			},
		}).fire();
		onChange(selectedSortOption);
	};

	return (
		<Box xcss={styles.wrapper}>
			<Text size="small" weight="medium" color="color.text.subtlest">
				<FormattedMessage {...messages.sortByLabel} />
			</Text>
			<Box xcss={styles.dropdownContainer}>
				<DropdownMenu
					appearance="tall"
					trigger={({ triggerRef, ...props }) => (
						<AkButton
							{...props}
							appearance="subtle"
							isDisabled={isDisabled}
							shouldFitContainer
							// NOTE: This will be unnecessary once we migrate to the new
							// button component in both, the page and block template panels.
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ textAlign: 'left' }}
							isSelected={isMenuOpen}
							iconAfter={<ChevronDownIcon label="" size="small" />}
							ref={triggerRef}
						>
							{optionLabels[value]}
						</AkButton>
					)}
					isOpen={isMenuOpen}
					onOpenChange={handleOpenChange}
					placement="bottom-start"
					shouldFlip={false}
				>
					<DropdownItemGroup>
						<DropdownItem onClick={handleSortOrderChange(SnippetSortOrder.CREATED_AT)}>
							<FormattedMessage {...messages.createdAtLabel} />
						</DropdownItem>
						<DropdownItem onClick={handleSortOrderChange(SnippetSortOrder.LAST_UPDATED)}>
							<FormattedMessage {...messages.lastUpdatedLabel} />
						</DropdownItem>
						<DropdownItem onClick={handleSortOrderChange(SnippetSortOrder.POPULARITY)}>
							<FormattedMessage {...messages.popularityLabel} />
						</DropdownItem>
						<DropdownItem onClick={handleSortOrderChange(SnippetSortOrder.ALPHABETICAL)}>
							<FormattedMessage {...messages.alphabeticalLabel} />
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</Box>
		</Box>
	);
};
